import { he } from "date-fns/locale";
import { format, getTime, formatDistanceToNow, isValid } from "date-fns";

// ----------------------------------------------------------------------

type DateInput = Date | string | number | null | undefined;

// Normalizes the many shapes a date can arrive in (Date, ISO string, epoch ms,
// or a Firestore Timestamp-like object serialized over JSON) into a valid Date.
// Returns null when the input can't be parsed — callers fall back gracefully
// instead of date-fns throwing "RangeError: Invalid time value".
function toValidDate(date: DateInput): Date | null {
  if (date === null || date === undefined || date === "") return null;

  // Firestore Timestamp instance or its JSON-serialized shapes.
  if (typeof date === "object" && !(date instanceof Date)) {
    const anyDate = date as any;
    if (typeof anyDate.toDate === "function") {
      const d = anyDate.toDate();
      return isValid(d) ? d : null;
    }
    const seconds = anyDate.seconds ?? anyDate._seconds;
    if (typeof seconds === "number") {
      const d = new Date(seconds * 1000);
      return isValid(d) ? d : null;
    }
    return null;
  }

  const parsed = new Date(date);
  return isValid(parsed) ? parsed : null;
}

export function fDate(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "dd MMM yyyy", {
    locale: rtl ? he : undefined
  });
}

export function fDateWithoutDay(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "MMM yyyy", {
    locale: rtl ? he : undefined
  });
}

export function fDateTime(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "dd MMM yyyy HH:mm:ss", {
    locale: rtl ? he : undefined
  });
}

export function fDateTimeWithoutSecond(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "dd MMM yyyy HH:mm", {
    locale: rtl ? he : undefined
  });
}

export function fTime(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "HH:mm:ss", {
    locale: rtl ? he : undefined
  });
}

export function fTimeWithoutSecond(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "HH:mm", {
    locale: rtl ? he : undefined
  });
}

export function fTimestamp(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return NaN;
  return getTime(d);
}

export function fDateTimeSuffix(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return format(d, "dd/MM/yyyy HH:mm", {
    locale: rtl ? he : undefined
  });
}

export function fToNow(date: DateInput, rtl: boolean = false) {
  const d = toValidDate(date);
  if (!d) return "";
  return formatDistanceToNow(d, {
    addSuffix: true,
    locale: rtl ? he : undefined
  });
}

export function fMiliseconds(miliseconds: number) {
  const date = new Date(miliseconds);
  return date.toISOString().slice(14, 19);
}
